import React from "react";

interface CountdownTimerProps {
  expiresAt: string;
  onExpire: () => void;
}

const CountdownTimer = ({ expiresAt, onExpire }: CountdownTimerProps) => {
  const [timeLeft, setTimeLeft] = React.useState<{
    hours: number;
    minutes: number;
    seconds: number;
    total: number;
  }>({ hours: 0, minutes: 0, seconds: 0, total: 0 });

  React.useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const expiry = new Date(expiresAt).getTime();
      const diff = expiry - now;

      if (diff <= 0) {
        return { hours: 0, minutes: 0, seconds: 0, total: 0 };
      }

      return {
        hours: Math.floor(diff / (1000 * 60 * 60)),
        minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((diff % (1000 * 60)) / 1000),
        total: diff,
      };
    };

    setTimeLeft(calculateTimeLeft());

    const timer = setInterval(() => {
      const remaining = calculateTimeLeft();
      setTimeLeft(remaining);

      if (remaining.total <= 0) {
        clearInterval(timer);
        onExpire();
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [expiresAt, onExpire]);

  const pad = (n: number) => String(n).padStart(2, "0");

  const getTimerClassName = () => {
    const totalMinutes = timeLeft.total / (1000 * 60);
    if (totalMinutes <= 0) return "countdown-timer countdown-timer--expired";
    if (totalMinutes <= 15) return "countdown-timer countdown-timer--critical";
    if (totalMinutes <= 60) return "countdown-timer countdown-timer--warning";
    return "countdown-timer countdown-timer--ok";
  };

  if (timeLeft.total <= 0) {
    return null;
  }

  return (
    <div className={getTimerClassName()}>
      <div className="countdown-timer__label">Temps restant</div>
      <div className="countdown-timer__digits">
        {timeLeft.hours > 0 && (
          <>
            <span className="countdown-timer__segment">
              <span className="countdown-timer__number">{pad(timeLeft.hours)}</span>
              <span className="countdown-timer__unit">h</span>
            </span>
            <span className="countdown-timer__separator">:</span>
          </>
        )}
        <span className="countdown-timer__segment">
          <span className="countdown-timer__number">{pad(timeLeft.minutes)}</span>
          <span className="countdown-timer__unit">min</span>
        </span>
        <span className="countdown-timer__separator">:</span>
        <span className="countdown-timer__segment">
          <span className="countdown-timer__number">{pad(timeLeft.seconds)}</span>
          <span className="countdown-timer__unit">s</span>
        </span>
      </div>
    </div>
  );
};

export default CountdownTimer;
